import React,{useState, useEffect} from "react";
import {Box} from "@mui/material";
import styled from "@emotion/styled";
import TopBar from "../../../../../component/TopBar";
import BtnPrimary from "../../../../../component/PrimaryButton";
import LeaveData from "./LeaveData";
import Calendar from "../../../../../component/CalendarMark";
import ApplyLeave from "./ApplyLeave";
import {getleaveDetails, getSelfLeaveBalance, getAllLeaveType} from "../../../../../services/leave";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import Counter from "../../../../../component/Counter";
import { counterRowSx } from "../../../container/frameLayout";
import { balanceByType, totalBalance, balanceBreakdown, waitingLine } from "./leaveFacts";
import { useNotify } from "../../../../../component/NotificationProvider";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import { tokens, displayFont } from "../../../../../theme";
import EncashLeave, { EncashmentRequests, getSelfEncashments } from "./EncashLeave";

const MainBox = styled(Box)({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#FBF2EC",
    padding: "1.5rem",
});

const TextBox = styled(Box)({
    display: "block",
  });

const Leave = () => {

 const notify = useNotify();
 const [open, setOpen] = useState(false);
 const [leaveData, setLeaveData] = useState([]);
 const [balance, setBalance] = useState(null); // null = unknown, number = total available
 const [loading, setLoading] = useState(true);
 const [loadError, setLoadError] = useState(false);
 const [encashments, setEncashments] = useState([]);
 const [leaveTypes, setLeaveTypes] = useState([]);
 // raw balances, kept alongside the total so the band can name each type
 const [balances, setBalances] = useState([]);
 const { data: self } = useSelfEmployee();

 const handleClickOpen = () => {
   setOpen(true);
 };

 const handleClose = () => {
   setOpen(false);
   fetchData();
 };

 useEffect(() => {
  fetchData();
}, []); // eslint-disable-line react-hooks/exhaustive-deps

const fetchData = async () => {
  setLoading(true);
  setLoadError(false);
  try {
    const res = await getleaveDetails();
    if (res?.success === false) {
      throw new Error(res?.message || "Failed to load leave details");
    }
    setLeaveData(Array.isArray(res?.data) ? res.data : []);
  } catch (error) {
    setLoadError(true);
    notify.error(error?.message || "Failed to load leave details");
  } finally {
    setLoading(false);
  }
  // leave balance is independent — a 404 (no record yet) just leaves it unknown
  try {
    const balRes = await getSelfLeaveBalance();
    if (balRes?.success !== false && Array.isArray(balRes?.data)) {
      setBalances(balRes.data);
      setBalance(totalBalance(balRes.data));
    }
  } catch (e) { /* non-blocking */ }
  // Leave types name the balances and the requests. A balance is an id and a
  // number on its own, so without these the band can only say a total.
  try {
    const typeRes = await getAllLeaveType();
    if (Array.isArray(typeRes?.data)) setLeaveTypes(typeRes.data);
  } catch (e) { /* non-blocking */ }
  // own leave-encashment requests (non-blocking)
  try {
    const encRes = await getSelfEncashments();
    if (Array.isArray(encRes?.data)) setEncashments(encRes.data);
  } catch (e) { /* non-blocking */ }
};
// Calculate leave approved and leave request pending
const leaveApprovedCount = leaveData.filter(item => item.status === 'Approved').length;
const leavePendingCount = leaveData.filter(item => item.status === 'Pending').length;

// Tokens-styled summary cards
/*
  The band names each balance rather than lumping them into one figure.
  "18 days left" is not a number anybody can act on: casual and sick leave are
  not interchangeable, and which one an employee has left decides whether they
  can take next Friday off. The book draws these as bars against an annual
  entitlement, which this product does not expose — so they are figures.
*/
const namedBalances = balanceByType(balances, leaveTypes);
const typeById = Object.fromEntries(
  (leaveTypes || []).map((t) => [String(t?._id), t?.name || t?.leaveCode || "Leave"])
);
// Four is the band's minimum, so the named balances lead and the request
// counts fill the rest — never the other way round, because what is left is
// what somebody came here to find out.
const counters = [
  ...namedBalances.slice(0, 2).map((b) => ({
    label: `${b.name} left`,
    value: b.quantity,
    sub: b.isPaid ? "days available" : "days available, unpaid",
  })),
  { label: "Total left", value: balance == null ? "—" : balance,
    sub: namedBalances.length ? balanceBreakdown(namedBalances) : "across every type" },
  { label: "Pending requests", value: leavePendingCount,
    sub: leavePendingCount ? "waiting on your manager" : "nothing waiting",
    tone: leavePendingCount ? tokens.warningText : undefined },
  { label: "Approved", value: leaveApprovedCount, sub: "booked or taken" },
].slice(0, 5);

return (
    <Box sx={{width:"100%"}}>
       <TopBar
         MainHeading="Leave"
         breadcrumbs={[{ label: "Leave" }]}
         meta={loading ? null : waitingLine(leaveData, self?.reportingManager)}
       />
       <MainBox>
           <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%'}}>
             <TextBox sx={{fontWeight: 600, fontFamily: displayFont, fontSize: "1.25rem", color:tokens.text}}>
               Leave Balance
             </TextBox>
             <Box sx={{ display:'flex', gap:'0.5rem' }}>
               <EncashLeave onChanged={fetchData} />
               <BtnPrimary sx={{ borderRadius: '8px'}} onClick={handleClickOpen}>
                 + Apply Leave
               </BtnPrimary>
             </Box>
           </Box>
           <Box sx={{ ...counterRowSx(counters.length), marginTop: '1.25rem', marginBottom: '1.5rem' }}>
                 {counters.map((item) => (
                   <Counter key={item.label} label={item.label} value={item.value} sub={item.sub} tone={item.tone} />
                 ))}
           </Box>
           <Box sx={{display:'flex', justifyContent:'space-between', gap: "1.5rem"}}>
                {loading ? (
                  <Box sx={{flex:1}}><SectionLoader/></Box>
                ) : loadError ? (
                  <Box sx={{flex:1}}>
                    <EmptyState variant="error" title="Couldn't load leave requests" onRetry={fetchData}/>
                  </Box>
                ) : leaveData.length === 0 ? (
                  <Box sx={{flex:1}}>
                    <EmptyState title="No leave requests yet" description="Apply for leave to see it here."/>
                  </Box>
                ) : (
                  <LeaveData
                    leaveData={leaveData}
                    typeById={typeById}
                    loading={loading}
                    onChanged={fetchData}
                  />
                )}
                <Box sx={{flex:1}}>
                     <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem'}}>
                            <TextBox sx={{color:"#2A1D22", fontWeight:500,fontSize:'1.125rem', lineHeight:'1.68rem' }}>Holidays</TextBox>
                            <TextBox sx={{fontSize:'0.875rem', lineHeight:'1.31rem', color:"#2A1D22"}}>Months</TextBox>
                     </Box>
                     <Box sx={{borderRadius:"12px", backgroundColor:tokens.card,boxShadow: "0 16px 40px -20px rgba(42,29,34,0.22)"}}>
                            <Calendar/>
                     </Box>
                </Box>
           </Box>
           <EncashmentRequests requests={encashments} />
       </MainBox>
          <ApplyLeave open={open} onClose={handleClose} />
    </Box>
  );
};

export default Leave;